/**
 * Check if a plugin has a valid slug that can be used for updates
 * Uses the same logic as getPluginSlug to detect valid slugs
 * 
 * @param {Object} plugin - The plugin object
 * @returns {boolean} - True if plugin has a valid slug, false otherwise
 */
function hasValidSlug(plugin) {
  if (!plugin) return false;
  
  const candidates = [
    plugin.slug,
    plugin.plugin,
    plugin.plugin_file,
    plugin.text_domain,
    plugin.textDomain,
    plugin.name
  ];
  
  for (const candidate of candidates) {
    if (!candidate) continue;
    const sanitized = String(candidate).trim().toLowerCase();
    if (!sanitized || sanitized === '.' || sanitized === 'unknown') continue;
    // Extract slug part (before / if exists)
    const slug = sanitized.split('/')[0];
    if (slug && slug.length > 0) {
      return true;
    }
  }
  
  return false;
}

function hasMetadataDrivenUpdate(plugin) {
  if (!plugin) return false;

  // Get current version
  const currentVersion = plugin.version || plugin.Version || null;
  
  // If no current version, we can't determine if update is available
  if (!currentVersion) {
    // But if hasUpdate is explicitly true, return true
    return plugin.hasUpdate === true || plugin.updateAvailable === true;
  }

  // Check explicit boolean flags first
  if (plugin.hasUpdate === true || plugin.updateAvailable === true) {
    return true;
  }

  // Check if plugin.update object exists and has new_version
  if (plugin.update) {
    if (typeof plugin.update === 'object') {
      // Check new_version field
      if (plugin.update.new_version) {
        const newVersion = String(plugin.update.new_version).trim();
        const current = String(currentVersion).trim();
        if (newVersion && newVersion !== current && newVersion !== '0.0.0') {
          return true;
        }
      }
      // Check version field in update object
      if (plugin.update.version) {
        const newVersion = String(plugin.update.version).trim();
        const current = String(currentVersion).trim();
        if (newVersion && newVersion !== current && newVersion !== '0.0.0') {
          return true;
        }
      }
    }
  }

  // Check available_version
  if (plugin.available_version) {
    const availableVersion = String(plugin.available_version).trim();
    const current = String(currentVersion).trim();
    if (availableVersion && availableVersion !== current && availableVersion !== '0.0.0') {
      return true;
    }
  }

  // Check latest_version
  if (plugin.latest_version) {
    const latestVersion = String(plugin.latest_version).trim();
    const current = String(currentVersion).trim();
    if (latestVersion && latestVersion !== current && latestVersion !== '0.0.0') {
      return true;
    }
  }

  // Check wporg object
  if (plugin.wporg) {
    if (plugin.wporg.new_version) {
      const newVersion = String(plugin.wporg.new_version).trim();
      const current = String(currentVersion).trim();
      if (newVersion && newVersion !== current && newVersion !== '0.0.0') {
        return true;
      }
    }
    if (plugin.wporg.version) {
      const newVersion = String(plugin.wporg.version).trim();
      const current = String(currentVersion).trim();
      if (newVersion && newVersion !== current && newVersion !== '0.0.0') {
        return true;
      }
    }
  }

  // Check wp_metadata object
  if (plugin.wp_metadata) {
    if (plugin.wp_metadata.new_version) {
      const newVersion = String(plugin.wp_metadata.new_version).trim();
      const current = String(currentVersion).trim();
      if (newVersion && newVersion !== current && newVersion !== '0.0.0') {
        return true;
      }
    }
    if (plugin.wp_metadata.version) {
      const newVersion = String(plugin.wp_metadata.version).trim();
      const current = String(currentVersion).trim();
      if (newVersion && newVersion !== current && newVersion !== '0.0.0') {
        return true;
      }
    }
  }

  return false;
}

/**
 * Returns true when WordPress metadata indicates a new version exists.
 * This is a thin wrapper kept for backwards compatibility and uses the
 * same strict rules as hasWordPressReportedUpdate().
 *
 * IMPORTANT: This deliberately ignores any guessed fields like
 * available_version/latest_version so that we only ever reflect what
 * WordPress core actually reports via its update API.
 */
export function hasMetadataUpdate(plugin) {
  return hasWordPressReportedUpdate(plugin);
}

/**
 * Check if WordPress core reports this plugin has an update available.
 * This ONLY checks WordPress update API data, not forced updates or slugs.
 * Used for accurate update counts that match WordPress backend.
 * 
 * @param {Object} plugin - The plugin object
 * @returns {boolean} - True if WordPress reports update.available === true
 */
export function hasWordPressReportedUpdate(plugin) {
  if (!plugin) return false;

  // WordPress reports updates via the 'update' object with 'new_version'
  // This is the authoritative source from WordPress core
  if (plugin.update && typeof plugin.update === 'object') {
    const newVersion = plugin.update.new_version || plugin.update.newVersion;
    const currentVersion = plugin.version || plugin.Version || '0.0.0';
    
    // Only return true if:
    // 1. new_version exists
    // 2. new_version is different from current version
    // 3. new_version is not '0.0.0' (invalid)
    if (newVersion) {
      const newVersionStr = String(newVersion).trim();
      const currentVersionStr = String(currentVersion).trim();
      
      if (newVersionStr && 
          newVersionStr !== currentVersionStr && 
          newVersionStr !== '0.0.0' &&
          currentVersionStr !== '0.0.0') {
        return true;
      }
    }
  }

  // Also check explicit hasUpdate flag if it's set by WordPress
  // But only if update object exists (WordPress-reported)
  if (plugin.hasUpdate === true && plugin.update) {
    return true;
  }

  return false;
}

/**
 * Comprehensive function to detect if a plugin has an update available.
 *
 * REAL WORDPRESS UPDATES ONLY:
 * - We now rely exclusively on WordPress core update metadata
 *   (the same data wp-admin uses), via hasWordPressReportedUpdate().
 * - No guessing based on slug, latest_version, available_version,
 *   or any auto‑update flags.
 */
export function isUpdateAvailable(plugin) {
  return hasWordPressReportedUpdate(plugin);
}

/**
 * Get the new version string for a plugin if available
 * @param {Object} plugin - The plugin object
 * @returns {string|null} - The new version string or null
 */
export function getNewVersion(plugin) {
  if (!plugin) return null;

  // Check all possible fields in order of preference
  const candidates = [
    plugin.update?.new_version,
    plugin.update?.version,
    plugin.available_version,
    plugin.latest_version,
    plugin.wporg?.new_version,
    plugin.wporg?.version,
    plugin.wp_metadata?.new_version,
    plugin.wp_metadata?.version
  ];

  for (const candidate of candidates) {
    if (candidate && String(candidate).trim() && candidate !== '0.0.0') {
      return String(candidate).trim();
    }
  }

  return null;
}

